<template>
  <div class="bg-gray-50 py-12 sm:py-18">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <!-- Section Title & Subtitle -->
      <div class="text-center">
        <h2 class="text-base font-semibold text-indigo-600">{{ t('specialty_highlights.title') }}</h2>
        <p class="mt-2 text-4xl font-bold leading-10 tracking-tight text-gray-900 sm:text-5xl">
          {{ t('specialty_highlights.subtitle') }}
        </p>
        <!-- (Optional) short section description -->
        <p class="mx-auto mt-4 max-w-2xl text-lg leading-7 text-gray-600">
          {{ t('specialty_highlights.description') }}
        </p>
      </div>

      <!-- Responsive Grid -->
      <div class="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div
          v-for="(spec, i) in allSpecialties"
          :key="i"
          class="flex flex-col overflow-hidden rounded-xl bg-white p-6 shadow transition-shadow hover:shadow-lg"
        >
          <!-- Specialty Name & Subtitle -->
          <div class="mb-3">
            <h3 class="text-xl font-semibold text-gray-900">
              {{ displayName(spec) }}
            </h3>
            <p class="mt-1 text-sm font-medium text-indigo-600">
              {{ spec.en_name }}
            </p>
          </div>

          <!-- Common Illnesses as a bulleted list -->
          <div class="flex-grow">
            <h4 class="text-sm font-semibold text-gray-700">
              {{ t('specialty_highlights.common_illnesses') }}
            </h4>
            <ul class="mt-2 list-inside list-disc space-y-1 text-sm text-gray-600">
              <li v-for="(illness, idx) in spec.common_illnesses" :key="idx">
                {{ displayIllness(illness) }}
              </li>
            </ul>
          </div>

          <!-- CTA / More Info Button -->
          <div class="mt-6">
            <NuxtLink
              :to="localePath('/specialty/' + spec.en_name)"
              class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {{ t('specialty_highlights.learn_more') }} {{ displayName(spec) }}
            </NuxtLink>
          </div>
        </div>
      </div>

      <!-- Button to See All Specialties -->
      <div class="mt-8 text-center">
        <NuxtLink
          :to="localePath('/specialty')"
          class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          {{ t('specialty_highlights.view_all') }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '#i18n'
import { tify } from 'chinese-conv'

const props = defineProps({
  specialty_data: {
    type: Array,
    required: true,
  },
})

// i18n & routing helpers
const { locale, t } = useI18n()
const localePath = useLocalePath()

// All specialties
const allSpecialties = computed(() => props.specialty_data || [])

// Display name based on locale
function displayName(spec) {
  if (locale.value === 'en-US') return spec.en_name
  if (locale.value === 'zh-Hant') return tify(spec.cn_name)
  return spec.cn_name
}

// Display illness list item based on locale
function displayIllness(illness) {
  if (locale.value === 'zh-Hant') return tify(illness)
  if (locale.value === 'en-US') {
    const key = `illness.${illness}`
    const translated = t(key)
    return translated !== key ? translated : illness
  }
  return illness
}
</script>
